import { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isHome = location.pathname === '/';

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setIsSearchOpen(false);
    setSearchQuery('');
  }, []);

  return (
    <header className="relative z-50" role="banner">
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isHome
            ? 'bg-transparent'
            : 'bg-background-50 border-b border-background-200/70'
        }`}
        role="navigation"
        aria-label="主导航"
      >
        <div className="px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center gap-2 group cursor-pointer"
              aria-label="西瓜视频首页"
            >
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                <i className="ri-play-circle-fill text-background-50 text-xl md:text-2xl"></i>
              </div>
              <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">
                西瓜视频
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname === '/'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                <i className="ri-home-4-line mr-1.5"></i>
                {t('nav.home')}
              </Link>

              <div className="relative group">
                <button
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    location.pathname.startsWith('/category')
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <i className="ri-grid-line mr-1.5"></i>
                  {t('nav.category')}
                </button>
                <div className="absolute top-full left-0 mt-1 w-48 bg-background-50 border border-background-200/70 rounded-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/about"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname === '/about'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                <i className="ri-information-line mr-1.5"></i>
                {t('nav.about')}
              </Link>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative hidden md:block">
                <form onSubmit={handleSearchSubmit}>
                  <div className="flex items-center bg-background-100 rounded-full px-3 py-2 border border-background-200/70 transition-all duration-200 focus-within:border-primary-300 focus-within:bg-background-50">
                    <i className="ri-search-line text-foreground-500 text-sm"></i>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('nav.searchPlaceholder')}
                      className="bg-transparent border-none outline-none text-sm text-foreground-900 placeholder-foreground-400 w-40 ml-2"
                      aria-label={t('nav.search')}
                    />
                  </div>
                </form>
              </div>

              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-search-line text-lg"></i>
              </button>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="菜单"
              >
                <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {isSearchOpen && (
          <div className="md:hidden px-4 pb-3 bg-background-50 border-b border-background-200/70">
            <form onSubmit={handleSearchSubmit}>
              <div className="flex items-center bg-background-100 rounded-full px-3 py-2 border border-background-200/70">
                <i className="ri-search-line text-foreground-500 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('nav.searchPlaceholder')}
                  className="bg-transparent border-none outline-none text-sm text-foreground-900 placeholder-foreground-400 flex-1 ml-2"
                  aria-label={t('nav.search')}
                />
              </div>
            </form>
          </div>
        )}

        {isMobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-b border-background-200/70 px-4 pb-4">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer ${
                  location.pathname === '/'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className="ri-home-4-line text-base"></i>
                {t('nav.home')}
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer ${
                    location.pathname === `/category/${cat.slug}`
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer ${
                  location.pathname === '/about'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className="ri-information-line text-base"></i>
                {t('nav.about')}
              </Link>
            </div>
          </div>
        )}
      </nav>
      <div className="h-16"></div>
    </header>
  );
}